import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Layout } from './Layout';
import { MetricCard } from './MetricCard';
import { AppContext } from '../src/App';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import {useSearchParams, useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { Separator } from './ui/separator';
import { Progress } from './ui/progress';
import {
  Home,
  ArrowLeft,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Eye,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Database,
  Bot,
  Timer,
  Activity,
  Zap,
  Settings,
  Users,
  Target,
  MessageSquare,
  User,
  Download,
  MoreHorizontal,
  RotateCcw,
  CircleUser,
  Undo,
  SearchXIcon,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  CalendarIcon
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { downloadAuditZip } from './ui/downloadHelpers';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { apiClient } from '@/Utils/apiClient';
import { getLastWeekRange } from '@/Utils/helpers';
import { ROUTES } from '@/routes/config';
// import { Dialog, DialogContent } from '@radix-ui/react-dialog';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle, Dialog, DialogContent } from './ui/dialog';
import { toast } from 'sonner';
import { PaginationCustom } from './ui/pagination-custom';
import { c } from 'node_modules/vite/dist/node/types.d-aGj9QkWt';
import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfYear,
} from 'date-fns';


// Add this interface inside your ClientClinicianView component function
interface CurrentIndexState {
  index: number;
  filterItems: any[];
  currentpage?: number;
  filters: {
    aiDecision: string;
    auditorDecision: string;
    KTStatus: string;
    searchTerm: string;
  };
}

export function ClientClinicianView() {
  const { user, token } = useAuth();
  // FIX: Removed unused currentPageHarvestRun from context, will use local state
  const { sideBarItems, setCurrentView, setSelectedHarvestRunData, setSelectedAuditData, setCurrentPageHarvestRuns, setCurrentIndexOfClinicianChart, currentIndexOfClinicianChart,setPreviousViewScreen,orgConfig } = useContext(AppContext);
  const navigate = useNavigate();
  const [harvestRuns, setHarvestRuns] = useState<any>({ audits: [], pagination: { totalCount: 0, totalPages: 1 } });
  const [searchParams] = useSearchParams();
  const [runId, setRunId] = useState(searchParams.get('runId'));
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const [dropdownVal, setDropdownVal] = useState<any[]>([])
  const [selectedRerunCharts, setSelectedRerunCharts] = useState<any>(null);
  const [selectedUndoCharts, setSelectedUndoCharts] = useState<any>(null);
  const auditorDecisions = ["AGREED", "DISAGREED", "REVIEW NEEDED", "ALL"
  ]
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRangeSelection, setDateRangeSelection] = useState("all");
  const [dateRange, setDateRange] = useState({
    from: startOfDay(subDays(new Date(), 29)),
    to: endOfDay(new Date()),
  });
  const [tempRange, setTempRange] = useState({
    from: startOfDay(subDays(new Date(), 29)),
    to: endOfDay(new Date()),
  });
  const aiDecisions = ["Approved", "Send For Correction", "ALL"]
  const kanTimeStatus = ["Pending", "No Action", "Updated", "ALL"]
  const chartType = ["CSC","DLS","SN","AIDE","All"]
  const quickDateRanges = [
    {
      label: "Today",
      value: {
        from: startOfDay(new Date()),
        to: endOfDay(new Date()),
      },
    },
    {
      label: "Last Week",
      value: getLastWeekRange()
    },
    {
      label: "Last 7 days",
      value: {
        from: startOfDay(subDays(new Date(), 6)),
        to: endOfDay(new Date()),
      },
    },
    {
      label: "Last 30 days",
      value: {
        from: startOfDay(subDays(new Date(), 29)),
        to: endOfDay(new Date()),
      },
    },
    {
      label: "This month",
      value: {
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
      },
    },
    {
      label: "Last month",
      value: {
        from: startOfMonth(subDays(startOfMonth(new Date()), 1)),
        to: endOfMonth(subDays(startOfMonth(new Date()), 1)),
      },
    },
    {
      label: "This year",
      value: {
        from: startOfYear(new Date()),
        to: endOfDay(new Date()),
      },
    },
  ];
  const chartReviewMenu = sideBarItems.find((item: any) => item.name === "Chart Review");
  const promptMenu = sideBarItems.find((item: any) => item.name === "Prompts");
  const [sortConfig, setSortConfig] = useState<{ sortBy: string | null; sortOrder: 'asc' | 'desc' | null }>({
   sortBy: null,
   sortOrder: null,
  });
  // FIX: Pagination state is now local
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const hasPermission = (permission: string) => {
    return chartReviewMenu?.userPermissions?.includes(permission);
  }
  const hasPromptPermission = (permission: string) => {
    return promptMenu?.userPermissions?.includes(permission);
  }
  const isAdmin = hasPermission("chart:create")
  const fetchPrompts = async () => {
    console.log('Fetching prompts...');
    try {
      const response = await apiClient('prompts', {
        method: 'GET',
        token
      })
      const data = await response.json();
      setDropdownVal(data?.data);
    }
    catch (error: any) {
      console.error('Error fetching categories:', error.message);
    }
  }
  // Debug logging
  useEffect(() => {
    if(hasPromptPermission('prompts:view')){
      fetchPrompts();
    }
  }, []);

  const handleSort = (field: string) => {
    setSortConfig((prev) => {
      // If clicking the same field
      if (prev.sortBy === field) {
        if (prev.sortOrder === 'asc') return { sortBy: field, sortOrder: 'desc' };
        if (prev.sortOrder === 'desc') return { sortBy: null, sortOrder: null }; // Reset
      }
      // If clicking a new field, start with asc
      return { sortBy: field, sortOrder: 'asc' };
    });
  };
  
  const renderSortableHeader = (label: string, fieldKey: string) => {
    const isSorted = sortConfig.sortBy === fieldKey;
    return (
      <div 
        className="flex items-center gap-2 cursor-pointer select-none group"
        onClick={() => handleSort(fieldKey)}
      >
        {label}
        <span className="text-gray-400">
          {isSorted && sortConfig.sortOrder === 'asc' && <ArrowUp className="w-4 h-4 text-[#758195]" />}
          {isSorted && sortConfig.sortOrder === 'desc' && <ArrowDown className="w-4 h-4 text-[#758195]" />}
          {(!isSorted) && (
            <ArrowUpDown className="w-4 h-4 text-[#758195]" />
          )}
        </span>
      </div>
    );
  };

  const applyDateRange = (range: { from: Date; to: Date }) => {
    setDateRange(range);
    setTempRange(range);
    setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
      ...prev,
      filters: {
        ...prev.filters,
        startDate: format(range.from, "yyyy-MM-dd"),
        endDate: format(range.to, "yyyy-MM-dd"),
      },
    }));
    setCurrentPage(1);
    setCurrentPageHarvestRuns(1);
  };

  const fetchData = async () => {
    setLoading(true)
    try {
      let filterItems: Record<string, any> = {};
      if (currentIndexOfClinicianChart?.filters.aiDecision || currentIndexOfClinicianChart?.filters.auditorDecision || 
        currentIndexOfClinicianChart?.filters.KTStatus || currentIndexOfClinicianChart?.filters.searchTerm || currentIndexOfClinicianChart?.filters.chartType ||
        currentIndexOfClinicianChart?.filters.startDate || currentIndexOfClinicianChart?.filters.endDate ||
        sortConfig.sortBy || sortConfig.sortOrder) {
        filterItems = {
          aiDecision: currentIndexOfClinicianChart?.filters.aiDecision !== 'ALL' ? currentIndexOfClinicianChart?.filters.aiDecision : null,
          auditorDecision: currentIndexOfClinicianChart?.filters.auditorDecision === 'AGREED' ? 'Approved' : currentIndexOfClinicianChart?.filters.auditorDecision === 'DISAGREED' ? 'Rejected' : currentIndexOfClinicianChart?.filters.auditorDecision === 'REVIEW NEEDED' ? 'Pending' : currentIndexOfClinicianChart?.filters.auditorDecision === 'ALL' ? null : '',
          ktStatus: currentIndexOfClinicianChart?.filters.KTStatus !== 'ALL' ? currentIndexOfClinicianChart?.filters.KTStatus : null,
          search: currentIndexOfClinicianChart?.filters.searchTerm,
          chartType: currentIndexOfClinicianChart?.filters.chartType !== 'All' ? currentIndexOfClinicianChart?.filters.chartType : null,
          startDate: currentIndexOfClinicianChart?.filters.startDate || null,
          endDate: currentIndexOfClinicianChart?.filters.endDate || null,
          sortBy : sortConfig.sortBy,
          sortOrder : sortConfig.sortOrder
        }
      }
      const params = new URLSearchParams();
      for (const key in filterItems) {
        if (filterItems[key] != null) { // Only append if the value exists
          params.append(key, filterItems[key]);
        }
      }

      // FIX: Add pagination parameters to the API call
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());

      // FIX: Pass the params object to the fetch request
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/all?${params.toString()}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      
      setHarvestRuns(data?.data)
      return data;
    } catch (error: any) {
      console.error('Fetch error:', error.message);
      return null;
    }
    finally{
      setLoading(false)
    }
  };

  // FIX: This useEffect now fetches data whenever filters OR the current page change.
  useEffect(() => {
    fetchData()
  }, [
    currentIndexOfClinicianChart.filters.auditorDecision,
    currentIndexOfClinicianChart.filters.aiDecision,
    currentIndexOfClinicianChart.filters.KTStatus,
    currentIndexOfClinicianChart.filters.searchTerm,
    currentIndexOfClinicianChart.filters.chartType,
    currentIndexOfClinicianChart.filters.startDate,
    currentIndexOfClinicianChart.filters.endDate,
    currentPage,
    sortConfig
  ])
  
  // clear filter
  const handleClearFilter = () => {
    setCurrentIndexOfClinicianChart({
      index: 0,
      filterItems: [],
      totalChart: 0,
      filters: {
        aiDecision: "",
        auditorDecision: "",
        KTStatus: "",
        searchTerm: "",
        chartType:"",
        startDate: "",
        endDate: ""
      }
    });
    setDateRangeSelection("all");
    setDateRange({
      from: startOfDay(subDays(new Date(), 29)),
      to: endOfDay(new Date()),
    });
    setTempRange({
      from: startOfDay(subDays(new Date(), 29)),
      to: endOfDay(new Date()),
    });
    // FIX: Reset page when filters are cleared
    setCurrentPage(1);
    setCurrentPageHarvestRuns(1);
    setSortConfig({ sortBy: null, sortOrder: null });

  }
  
  // Show loading/error state if no data is available
  if (!harvestRuns) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
          <div className="p-8">
            {/* Breadcrumb Navigation */}
            <div className="mb-6">
              <Breadcrumb>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#" onClick={() => setCurrentView('chat-harvest')}>
                      Chat Harvest
                    </BreadcrumbLink>
                  </BreadcrumbItem>
              </Breadcrumb>
            </div>

            {/* Error State */}
            <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
              <CardContent className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-gray-900 font-medium mb-2">No Harvest Run Selected</h3>
                  <p className="text-gray-600 mb-4">
                    No harvest run data was found. Please go back to the Chat Harvest dashboard and select a completed run to view details.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // Filter and pagination state
  // FIX: These local states are not used for filtering and can be removed
  // const [searchTerm, setSearchTerm] = useState('');
  // const [statusFilter, setStatusFilter] = useState('all');
  
  // FIX: Get pagination data from the API response
  const totalPages = harvestRuns.pagination.totalPages || 1;
  const paginatedData = harvestRuns.audits; // This is already the correct page of data
  
  // FIX: This logic is no longer needed as pagination is handled by the API
  // const startIndex = (currentPage - 1) * itemsPerPage;

  useEffect(() => {
    // Only update if filters have actually changed
    const filterValues = [
      currentIndexOfClinicianChart.filters.auditorDecision,
      currentIndexOfClinicianChart.filters.aiDecision,
      currentIndexOfClinicianChart.filters.KTStatus,
      currentIndexOfClinicianChart.filters.searchTerm,
      currentIndexOfClinicianChart.filters.chartType,
      currentIndexOfClinicianChart.filters.startDate,
      currentIndexOfClinicianChart.filters.endDate,
      sortConfig.sortBy,
      sortConfig.sortOrder
    ];
    const hasFilters = filterValues.some(val => val);
    
    // FIX: Type prev correctly
    setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
      ...prev,
      index: 0, // reset index when filters change
      filterItems: hasFilters ? harvestRuns.audits : [],
      totalChart :harvestRuns.pagination.totalCount
    }));
  }, [
    currentIndexOfClinicianChart.filters.auditorDecision,
    currentIndexOfClinicianChart.filters.aiDecision,
    currentIndexOfClinicianChart.filters.KTStatus,
    harvestRuns.audits, // FIX: Removed extra comma
    currentIndexOfClinicianChart.filters.searchTerm,
    currentIndexOfClinicianChart.filters.chartType,
    currentIndexOfClinicianChart.filters.startDate,
    currentIndexOfClinicianChart.filters.endDate,
    sortConfig
  ]);
  
  // FIX: This useEffect was based on unused local state and can be removed.
  // The page reset logic is now in the filter handlers.
  // React.useEffect(() => {
  //   ...
  // }, [searchTerm, statusFilter, totalPages]);


  const getReviewBadge = (suggestion: string) => {
    const newSug = suggestion?.toLocaleLowerCase()
    const suggestionConfig = {
      'approved': { color: 'bg-green-100 text-green-800 border-green-200' },
      'agreed': { color: 'bg-green-100 text-green-800 border-green-200' },
      'pending': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      'Needs Review': { color: 'bg-blue-100 text-blue-800 border-blue-200' },
      'disagreed': { color: 'bg-red-100 text-red-800 border-red-200' },
      'rejected': { color: 'bg-red-100 text-red-800 border-red-200' },
      'Non-Compliant': { color: 'bg-red-100 text-red-800 border-red-200' },
      'review needed': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200' }
    };

    const config = suggestionConfig[newSug as keyof typeof suggestionConfig] || suggestionConfig['Needs Review'];

    return (
      <Badge variant="outline" className={`${config.color} text-sm font-medium`}>
        {suggestion}
      </Badge>
    );
  };

  const handleDownload = async (auditId: string, chartId: string) => {
    downloadAuditZip({ id: auditId, token, chartId });
  };
  
  const handleReTrychart = async (chartData: any) => {
    setSelectedRerunCharts(chartData);
    const match = chartData.section.match(/^(Section \d+)/);
    const getCategory = match ? match[1] : chartData.section;

    setDropdownVal(dropdownVal.filter((item: any) => {
      if (getCategory == item.category && (item.status === "ACTIVE" || item.status === "TEST")) {
        return item;
      }
    }));
    setShowRunDialog(true);
  };
  
  const handleBackToHarvest = () => {
    console.log('Navigating back to chat-harvest');
    setSelectedHarvestRunData(undefined);
    setCurrentView('chat-harvest');
    handleClearFilter()
    setCurrentPageHarvestRuns(1)
  };
  
  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    handleClearFilter()
  }

  const handleGoalRowClick = (goal: any, index: number) => {
    console.log('Goal row clicked:', goal);
    
    // const absoluteIndex = (currentPage - 1) * itemsPerPage + index;
    // FIX: Type 'prev' correctly to avoid wiping state
    setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
      ...prev,
      currentPage: currentPage,
      index: index,
      totalChart : harvestRuns.pagination.totalCount
        // or whatever new value you want
    }))
    
    // FIX: selectedHarvestRunData is not available in this component, removing it.
    // let data = { ...goal, run_id: selectedHarvestRunData?.run_id } 
    let data = { ...goal ,run_id : paginatedData[index].harvest_run_id} // Send just the goal data
    setSelectedAuditData(data);
    setPreviousViewScreen("client-clinician-view");
    // if(goal.prompt_name === 'SN Charts'){
    //   setCurrentView('sn-chart-detail');
    // }else{
      setCurrentView('audit-goals-detail');
    // }
    const auditIdentifier = paginatedData[index].harvest_run_id;
    const chartIndex = {...currentIndexOfClinicianChart,currentPage: currentPage,
      index: index,
      totalChart : harvestRuns.pagination.totalCount}
    if (auditIdentifier) {
      navigate(ROUTES.auditDetail,{
      state: {
       auditData: data,
       previousScreen: 'client-clinician-view',
       currentchartIndex : chartIndex,
       auditId : auditIdentifier
       }
      });
    }
  };
  
  const UndoChartsDecision = async () => {
    console.log('selectedUndoCharts', selectedUndoCharts)
    try {
      const response = await apiClient(`audits/${selectedUndoCharts?.id}`, {
        method: 'PUT',
        token,
        body: {
          outcome: "Pending",
          supervisorComment: ""
        }
      })
      const data = await response.json();
      if (data?.success) {
        toast.success('Undo Decision successfully');
      }

    }
    catch (error: any) {
      toast.error('Undo Decision failed');
      console.error('Error fetching categories:', error.message);
    }
    finally {
      fetchData();
      setSelectedUndoCharts(null);
    }
  }

  const ReRunChart = async () => {
    try {
      // FIX: selectedHarvestRunData is not available. This API call may need to be revised
      // as it requires a harvestRunId. For now, I'll assume it's part of selectedRerunCharts.
      const harvestRunId = selectedRerunCharts?.harvest_run_id; 
      if (!harvestRunId) {
        toast.error("Cannot re-run chart: Missing harvest_run_id.");
        return;
      }

      const response = await apiClient(`harvest/runs?runFlag=1&harvestRunId=${harvestRunId}&chartIds=${selectedRerunCharts?.chart_id}&promptVersionId=${selectedPrompt}`,
        {
          method: 'POST',
          token
        }
      );
      const result = await response.json();

      const pollInterval = 40000;

      const intervalId = setInterval(async () => {
        try {
          // FIX: Same issue here, need harvestRunId
          const statusResponse = await apiClient(`harvest/runs/status/${harvestRunId}`, {
            method: 'GET',
            token
          });
          const statusData = await statusResponse.json();
          
          if (statusData?.status === 'FAILED') {
            clearInterval(intervalId);
            await fetchData();
            toast.error('Re-run failed. Please try again.');
          }

          if (statusData?.status === 'COMPLETED') {
            clearInterval(intervalId);
            toast.success('Re-run Successfully completed');
            await fetchData(); // call your data fetcher
          }
        } catch (err: any) {
          console.error('Polling error:', err);
          toast.error(`Polling error: ${err.message}`);
          clearInterval(intervalId); // Optional: stop polling on error
        }
      }, pollInterval);

    }
    catch (error: any) {
      toast.error(`Failed to start re-run: ${error.message}`);
    }
    finally {
      // Don't call fetchData() here, it will be called by the poller
      setSelectedRerunCharts(null);
      setSelectedPrompt(null);
    }
  }
  
  const toCamelCase = (str: string) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace(/(^\w|\s\w)/g, (match) => match.toUpperCase());
  };
  
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
        <div className="p-8">
          <div className="mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="#" onClick={handleBackToHarvest}>
                    Client Overview
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

           <div className="mb-6 ms-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="font-inter font-semibold text-2xl mb-2">
              Client Overview - All Batches
            </h1>
          </div>
        </div>

          {/* Audit Goals Data Table */}
          <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
            <CardHeader>
              <div className='flex items-center gap-2 justify-between'>
                <div className='flex-1  items-center gap-2'>
                  <div className="flex-1 min-w-[400px] w-full">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by Client Name,Clinician Name..."
                        value={currentIndexOfClinicianChart.filters.searchTerm}
                        // FIX: Type 'prev' and reset page
                        onChange={(e) => {
                          const searchTerm = e.target.value;
                          setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
                            ...prev,
                            filters: {
                              ...prev.filters,
                              searchTerm: searchTerm
                            }
                          }));
                          setCurrentPage(1);
                          setCurrentPageHarvestRuns(1);
                        }}
                        className="pl-10 w-full"
                      />
                    </div>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 bg-gray-40">
                    <CalendarIcon className="w-4 h-4 text-gray-500" />
                    <Select
                      value={dateRangeSelection}
                      onValueChange={(value) => {
                        if (value === "all") {
                          setDateRangeSelection("all");
                          setShowDatePicker(false);
                          setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
                            ...prev,
                            filters: {
                              ...prev.filters,
                              startDate: "",
                              endDate: "",
                            },
                          }));
                          setCurrentPage(1);
                          setCurrentPageHarvestRuns(1);
                          return;
                        }
                        if (value === "custom") {
                          setDateRangeSelection("custom");
                          setTempRange(dateRange);
                          setShowDatePicker(true);
                          return;
                        }
                        const range = quickDateRanges.find((r) => r.label === value);
                        if (range) {
                          setDateRangeSelection(value);
                          applyDateRange(range.value);
                        }
                      }}
                      onOpenChange={(open) => {
                        if (!open && dateRangeSelection === "custom") {
                          setShowDatePicker(true);
                        }
                      }}
                    >
                      <SelectTrigger className="w-28 text-gray-500 border-0 shadow-none focus:ring-0 h-auto p-0 bg-transparent">
                        <SelectValue placeholder="Date Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dates</SelectItem>
                        {quickDateRanges.map((range) => (
                          <SelectItem key={range.label} value={range.label}>
                            {range.label}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-gray-500 font-medium">
                      {dateRangeSelection === "all"
                        ? "All"
                        : `${format(dateRange.from, "MMM dd")} — ${format(dateRange.to, "MMM dd")}`}
                    </span>
                  </div>
                   <div>
                    <Select value={currentIndexOfClinicianChart?.filters.chartType} onValueChange={(value) => {
                      // FIX: Type 'prev' and reset page
                      setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          chartType: value
                        }
                      }));
                      setCurrentPage(1);
                      setCurrentPageHarvestRuns(1);
                    }}>
                      <SelectTrigger className="w-42">
                        <SelectValue placeholder="Chart Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {chartType.map((category) => (
                          <SelectItem key={category} value={category}>
                            <span className='uppercase font-medium text-gray-600'>{category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select value={currentIndexOfClinicianChart.filters.aiDecision} onValueChange={(value) => {
                      // FIX: Type 'prev' and reset page
                      setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          aiDecision: value
                        }
                      }));
                      setCurrentPage(1);
                      setCurrentPageHarvestRuns(1);
                    }}>
                      <SelectTrigger className="w-42">
                        <SelectValue placeholder="AI Decisions" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiDecisions.map((category) => (
                          <SelectItem key={category} value={category}>
                            <span className=' font-inter uppercase font-medium text-gray-600'>{category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select></div>
                  <div>
                    <Select value={currentIndexOfClinicianChart.filters.auditorDecision} onValueChange={(value) => {
                      // FIX: Type 'prev' and reset page
                      setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          auditorDecision: value
                        }
                      }));
                      setCurrentPage(1);
                      setCurrentPageHarvestRuns(1);
                    }}>
                      <SelectTrigger className="w-42">
                        <SelectValue placeholder="Auditor Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {auditorDecisions.map((category) => (
                          <SelectItem key={category} value={category}>
                            <span className='uppercase font-medium text-gray-600'>{category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select value={currentIndexOfClinicianChart?.filters.KTStatus} onValueChange={(value) => {
                      // FIX: Type 'prev' and reset page
                      setCurrentIndexOfClinicianChart((prev: CurrentIndexState) => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          KTStatus: value
                        }
                      }));
                      setCurrentPage(1);
                      setCurrentPageHarvestRuns(1);
                    }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder={`${orgConfig?.name} Status`} />
                      </SelectTrigger>
                      <SelectContent>
                        {kanTimeStatus.map((category) => (
                          <SelectItem key={category} value={category}>
                            <span className='uppercase font-medium text-gray-600'>{category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button
                      variant="outline"
                      onClick={handleClearFilter}
                      className="font-inter font-semibold text-[14px] leading-[20px] tracking-normal text-[#2C3E5D]"
                    >
                      <RefreshCw className="w-4 h-4 mr-2 text-[#2C3E5D]" />
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <Popover open={showDatePicker}>
              <PopoverTrigger asChild>
                <div></div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-4">
                  <div className="text-xs font-medium mb-2">
                    Select Date Range
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground">
                        From
                      </div>
                      <Calendar
                        className="scale-90"
                        classNames={{
                          day: "size-6 p-0 text-xs",
                          head_cell: "w-6 text-[10px]",
                          caption_label: "text-xs",
                        }}
                        mode="single"
                        selected={tempRange.from}
                        onSelect={(date) => date && setTempRange((prev) => ({ ...prev, from: date }))}
                        initialFocus
                        disabled={{ after: new Date() }}
                      />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">
                        To
                      </div>
                      <Calendar
                        className="scale-90"
                        classNames={{
                          day: "size-6 p-0 text-xs",
                          head_cell: "w-6 text-[10px]",
                          caption_label: "text-xs",
                        }}
                        mode="single"
                        selected={tempRange.to}
                        onSelect={(date) => date && setTempRange((prev) => ({ ...prev, to: date }))}
                        disabled={{ after: new Date() }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowDatePicker(false);
                        setTempRange(dateRange);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (tempRange.from > tempRange.to) {
                          toast.error("To Date must be later than the From Date.");
                          return;
                        }
                        applyDateRange(tempRange);
                        setDateRangeSelection("custom");
                        setShowDatePicker(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <CardContent className="p-0">
              <div className="overflow-x-auto mx-6">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">{renderSortableHeader("Client id", "client_id")}</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">{renderSortableHeader("CLIENT", "client_name")}</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Visit Date</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]  ">{renderSortableHeader("Clinician", "clinician_name")}</TableHead>
                      {isAdmin && <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Chart Type</TableHead>}
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]  text-wrap text-center">AI Decision</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]  text-wrap">Auditor Review Status</TableHead>
                      {isAdmin && <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Auditor Comments</TableHead>}
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">{`${orgConfig?.name} Status`}</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195] text-center ">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center p-6">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : paginatedData?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          className="p-12 text-center text-gray-600"
                        >
                          No Data Found
                        </TableCell>
                      </TableRow>
                    ) : paginatedData?.map((goal: any, index: number) => {
                      const outcome = goal?.outcome?.toLowerCase();
                      let reviewLabel;
                      if (outcome === "approved") reviewLabel = "AGREED";
                      else if (outcome === "rejected") reviewLabel = "DISAGREED";
                      else reviewLabel = "REVIEW NEEDED";
                      return (
                        <TableRow
                          key={goal?.id}
                          className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                          onClick={() => {
                            if (hasPermission("chart:view")) { handleGoalRowClick(goal, index); }
                            else toast.error("You need 'audits:view' permission to view chart details.");
                          }}
                          title="Click to view goal details"
                        >
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.client_id}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col text-wrap w-32">
                              <span className="font-medium text-gray-600">{goal?.client_name}</span>
                              <span className="text-xs text-gray-500">
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.visit_date}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.clinician_name}</span>
                            </div>
                          </TableCell>
                          {isAdmin && <TableCell className="max-w-[120px] align-top">
                            <div className="flex flex-col w-full">
                              <span className="font-medium text-gray-500 break-words whitespace-normal w-full">
                                {goal?.section}
                              </span>
                              {/* <span className="text-xs text-gray-600">
                                Version: {goal?.prompt_version}
                              </span> */}
                            </div>
                          </TableCell>}
                          {/* {isAdmin && <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600 capitalize">{toCamelCase(goal?.running_status) || ""}
                              </span>
                            </div>
                          </TableCell>} */}
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600 text-center">{goal?.overall_audit_decision}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className='flex flex-col gap-2 items-center '>
                              {getReviewBadge(reviewLabel)}
                            </div>
                          </TableCell>
                          {isAdmin && <TableCell>
                            <div className='flex flex-col gap-2'>
                              {goal?.supervisor_comment ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="cursor-help text-ellipsis overflow-hidden break-words whitespace-normal"
                                        onMouseEnter={(e) => e.stopPropagation()}
                                        style={{
                                          WebkitLineClamp: 2, 
                                          display: '-webkit-box', 
                                          WebkitBoxOrient: 'vertical',
                                          maxHeight: '2.5rem'
                                        }}
                                      >
                                        {goal.supervisor_comment}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="bg-black text-white p-3 rounded-md text-sm max-w-sm max-h-60 overflow-y-auto whitespace-pre-line shadow-lg border border-gray-300"
                                    >
                                      {goal.supervisor_comment}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : <div className='text-center'>-</div>}
                            </div>
                          </TableCell>}
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.kt_update || ""}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className='flex items-center justify-center'>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => { }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View
                                </DropdownMenuItem>
                                {hasPermission("chart:create") &&
                                  <>{goal?.running_status !== "COMPLETED" &&
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      handleReTrychart(goal);
                                    }}>
                                      <></>
                                      <RotateCcw className="w-4 h-4 mr-2 color-blue" />
                                      Re-Run Chart
                                    </DropdownMenuItem>
                                  }
                                    {hasPermission("audits:review") && (!goal?.kt_update || goal?.kt_update === "Pending") &&  (goal?.outcome === "Approved" || goal?.outcome === "Rejected") && <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedUndoCharts(goal);
                                    }}>
                                      <></>
                                      <Undo className="w-4 h-4 mr-2" />
                                      Undo Decision
                                    </DropdownMenuItem>}
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(goal.id, goal?.chart_id);
                                    }}>
                                      <></>
                                      <Download className="w-4 h-4 mr-2" />
                                      Download
                                    </DropdownMenuItem>
                                  </>}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>           
              </div>

               {totalPages > 1 && (<div className='px-4'><PaginationCustom
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={harvestRuns.pagination.totalCount}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  setCurrentPageHarvestRuns(page); // your own state logic
                }}
                onItemsPerPageChange={(value) => { 
                  // This component doesn't seem to support changing items per page
                  // If it does, you would also need to update itemsPerPage state
                  // and reset currentPage to 1
                }}
              /></div>)}
            </CardContent>
          </Card>
          <AlertDialog open={showRunDialog} onOpenChange={setShowRunDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  Re Run Chart
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Select an prompt to re run this chart. The chart will re-run using the selected AI prompt configuration.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div>
                <div className='flex gap-2 items-start bg-gray-100 p-2 rounded'>
                  <div>
                    <CircleUser className="w-6 h-6" />
                  </div>
                  <div className=''>
                    <p className='font-inter font-semibold text-base'>{selectedRerunCharts?.client_name} <span className="font-bold">{selectedRerunCharts?.client_id}</span></p>
                    <p className='font-inter text-gray-600 text-sm'><span>Clinician: </span>{selectedRerunCharts?.clinician_name}</p>
                    <p className='font-inter text-gray-500 text-xs'><span>Visit Date: </span>{selectedRerunCharts?.visit_date}</p>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-y-2'>
                <div className="font-semibold text-sm text-gray-800 ml-1">Select Prompt</div>
                <Select value={selectedPrompt} onValueChange={setSelectedPrompt}>
                  <SelectTrigger className="w-full truncate">
                    <SelectValue className="capitalize" placeholder="Select Batch" />
                  </SelectTrigger>
                  <SelectContent className='h-auto overflow-y-auto'>
                    {dropdownVal &&
                      dropdownVal?.map((value: any) => (
                        <SelectItem key={value.version_id} value={value.version_id} className="capitalize overflow-hidden">
                          {value.name}  <span className={`${value.status === "ACTIVE" ? "bg-green-600" : "bg-gray-600"} text-white text-xs px-2 py-1 rounded ml-2`}>{value.status}</span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setSelectedPrompt(null)}>Cancel</AlertDialogCancel>
                <AlertDialogAction className={`bg-blue-600 hover:bg-blue-700 ${!selectedPrompt ? 'pointer-events-none opacity-50' : ''}`} onClick={ReRunChart}>Start Re-Run</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Dialog open={!!selectedUndoCharts} onOpenChange={() => setSelectedUndoCharts(null)}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Client ID: {selectedUndoCharts?.client_id}</DialogTitle>
                <DialogDescription>
                  Are you sure you want to undo the decision of this chart?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedUndoCharts(null)}>
                  Close
                </Button>
                {/* FIX: Removed invalid variant="approve" */}
                <Button onClick={UndoChartsDecision}>
                  Undo
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </Layout>
  );
}